import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleErrors } from "../../../utils/errorHandler";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import { InputField } from "../../Common/FormElements/InputField";
import { TinyMCEEditorField } from "../../Common/FormElements/TinyMCEEditorField";
import { useFormValidation } from "../../../hooks/useFormValidation";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { fetchProductById } from "../../../services/productService";
import {
  createProductFaq,
  updateProductFaq,
  fetchProductFaqbyId,
} from "../../../services/productFaqService";

export const AddProductFaq = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedProductFaq,
  setEditMode,
}) => {
  const { productId } = useParams();

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [productName, setproductName] = useState("");

  const [content, setContent] = useState("");
  const navigate = useNavigate();
  const PageLevelAccessurl = "product/:productId/faq";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);
  // ✅ Form validation hook
  const {
    formErrors,
    isFormValid,
    registerField,
    validateAllFields,
    clearErrors,
    touchAllFields,
    getFieldLabel,
  } = useFormValidation();

  // ✅ Create refs for each field
  const questionRef = useRef(null);
  const answerRef = useRef(null);

  // ✅ Initialize all state properly
  const [formValues, setFormValues] = useState({
    ProductId: productId,
    Question: "",
    Answer: "",
  });

  // ✅ Register all fields
  useEffect(() => {
    registerField("Question", questionRef, "Question");
    registerField("Answer", answerRef, "Answer");
  }, [registerField]);

  // Add this useEffect to validate after formValues change
  useEffect(() => {
    // Only validate if form has been touched/submitted before
    if (Object.keys(formErrors).length > 0) {
      const timeoutId = setTimeout(() => {
        validateAllFields();
      }, 100); // Small delay to ensure refs are updated

      return () => clearTimeout(timeoutId);
    }
  }, [formValues, validateAllFields, formErrors]);

  // ✅ Fix page access check with proper dependencies
  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess || !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, navigate]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchProductById(productId);
        if (response && response.isSuccess && response.result) {
          setproductName(response.result.productName);
        }
      } catch (error) {
        handleErrors(error);
      }
    };
    fetchData();
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      if (editMode && initialData.id) {
        try {
          const data = await fetchProductFaqbyId(initialData.id);
          if (data && data.isSuccess && data.result) {
            setFormValues({
              ProductId: productId,
              Question: data.result.question,
              Answer: data.result.answer,
            });
            setContent(data.result.answer);
          }
        } catch (error) {
          handleErrors(error);
        }
      } else {
        setFormValues({ ProductId: productId, Question: "", Answer: "" });
      }
    };
    fetchData();
  }, [editMode, initialData]);

  const handleInputChange = (e) => {
    const { id, type, value, checked } = e.target;
    const inputValue = type === "checkbox" ? checked : value;

    setFormValues((prev) => {
      const newValues = {
        ...prev,
        [id]: inputValue,
      };
      return newValues;
    });
  };

  // ✅ Reset all form fields
  const resetAllFields = () => {
    const fieldRefs = {
      questionRef,
      answerRef,
    };
    Object.values(fieldRefs).forEach((ref) => {
      if (ref.current && ref.current.reset) {
        ref.current.reset();
      }
    });
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // ✅ Touch all fields to show validation errors
      touchAllFields();

      // ✅ Validate all fields using refs
      const isValid = validateAllFields();

      if (!isValid) {
        toast.error("Please fix all validation errors before submitting.");
        return;
      }

      try {
        if (editMode) {
          setIsButtonDisabled(true);
          var updateResponseData = await updateProductFaq({
            ...formValues,
            id: initialData.id,
          });
          // ✅ Clear form errors
          clearErrors();
          if (updateResponseData?.data.isSuccess) {
            toast.success(updateResponseData?.data.statusMessage);
          } else {
            toast.error(updateResponseData?.data.statusMessage);
          }
        } else {
          setIsButtonDisabled(true);
          var createResponseData = await createProductFaq(formValues);
          resetAllFields();
          setFormValues({
            ProductId: productId,
            Question: "",
            Answer: "",
          });
          // ✅ Clear form errors
          clearErrors();
          if (createResponseData?.data.isSuccess) {
            toast.success(createResponseData?.data.statusMessage);
          } else {
            toast.error(createResponseData?.data.statusMessage);
          }
        }
        if (onSuccess) onSuccess();
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [formValues, editMode, initialData, onSuccess, setEditMode]
  );

  const handleAddNewClick = () => {
    setFormValues({
      ProductId: productId,
      Question: "",
      Answer: "",
    });
    resetAllFields();
    setSelectedProductFaq(null);
    setEditMode(false);
  };
  const handleEditorChange = (content) => {
    setContent(content);
    setFormValues((prevData) => ({
      ...prevData,
      Answer: content,
    }));
  };

  return (
    <>
      <ComponentHeader title="Manage Product Faq" />
      {Object.keys(formErrors).length > 0 && (
        <div className="alert alert-warning mb-3">
          <strong>Please fix the following errors:</strong>
          <ul className="mb-0 mt-2">
            {Object.entries(formErrors).map(([fieldId, error]) => (
              <li key={fieldId}>
                <strong>{getFieldLabel(fieldId)}:</strong> {error}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="row">
        <div className="col-xxl-12">
          <div className="card mt-xxl-n5">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-sm-1 mt-sm-1">
                {editMode ? "Update Product Faq" : "Add Product Faq"}
              </h5>
              <span className=" badge bg-primary fs-12">
                {productName
                  ? `Product: ${productName}`
                  : "No Product Selected"}{" "}
              </span>
            </div>

            <div className="card-body p-4">
              <form onSubmit={handleSubmit} method="POST">
                <div className="row">
                  <div className="col-lg-12 col-md-6 col-sm-12">
                    <InputField
                      ref={questionRef}
                      label="Question"
                      id="Question"
                      value={formValues.Question}
                      placeholder="Enter your Question"
                      onChange={handleInputChange}
                      required={true}
                      maxLength={3000}
                      showError={true}
                    />
                  </div>

                  <div className="col-lg-12 col-md-6 col-sm-12">
                    <div className="">
                      <TinyMCEEditorField
                        ref={answerRef}
                        id="Answer"
                        label="Answer"
                        content={content}
                        handleEditorChange={handleEditorChange}
                        required={true}
                        height={300}
                      />
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div>
                      <button
                        type="submit"
                        className="btn btn-secondary pt-1 pb-1 p-3"
                        disabled={isButtonDisabled || !isFormValid}
                      >
                        {isButtonDisabled
                          ? editMode
                            ? "Updating"
                            : "Saving"
                          : editMode
                          ? "Update"
                          : "Save"}
                      </button>
                      {editMode && (
                        <button
                          type="button"
                          onClick={handleAddNewClick}
                          className="btn btn-danger ms-1 pt-1 pb-1 p-3"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
